// Administration: approval rules, departments, spending categories, church settings, and people.
// Everything here is for central treasury (admin). The database enforces that: a department
// treasurer calling these simply gets refused. Money is ALWAYS integer ngwee here.
//
// Creating accounts and changing roles goes through the `admin-users` Edge Function (the only place
// that holds the service-role key); see callAdminUsers below.
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'

// ---------------------------------------------------------------- approval rules
export type ApprovalMode = 'record_only' | 'budgets_only' | 'budgets_and_expenses_over_threshold' | 'budgets_and_all_expenses'

export const APPROVAL_MODE_LABELS: Record<ApprovalMode, { title: string; detail: string }> = {
  record_only: { title: 'Record only', detail: 'Nothing needs approval. Departments just keep records.' },
  budgets_only: { title: 'Budgets only', detail: 'Budgets need approval. Expenses only when they are not in the budget.' },
  budgets_and_expenses_over_threshold: { title: 'Budgets and big expenses', detail: 'Budgets need approval. Expenses need it at or above the amount you set, or when not in the budget.' },
  budgets_and_all_expenses: { title: 'Budgets and every expense', detail: 'Everything needs central treasury approval first. The strictest setting.' },
}

export interface ApprovalPolicy {
  id: string
  mode: ApprovalMode
  thresholdNgwee: number
  effectiveFrom: string
  note: string | null
}

type PolicyRow = { id: string; mode: ApprovalMode; expense_threshold_ngwee: number; effective_from: string; note: string | null }
const toPolicy = (r: PolicyRow): ApprovalPolicy => ({ id: r.id, mode: r.mode, thresholdNgwee: Number(r.expense_threshold_ngwee), effectiveFrom: r.effective_from, note: r.note })

/** Every rule that has been in force, newest first. The first one is the rule in force now. */
export async function listApprovalPolicies(): Promise<ApprovalPolicy[]> {
  const res = await supabase.from('approval_policy').select('id, mode, expense_threshold_ngwee, effective_from, note').order('effective_from', { ascending: false })
  return (unwrap(res as never, 'Could not load the approval rules.') as PolicyRow[]).map(toPolicy)
}

/**
 * Change the approval rule from now on. Nothing already approved is undone; if the new rule is
 * looser, items waiting for a decision that no longer need one are approved automatically.
 */
export async function setApprovalPolicy(p: { mode: ApprovalMode; thresholdNgwee: number; note?: string }): Promise<void> {
  if (p.mode === 'budgets_and_expenses_over_threshold' && (!Number.isSafeInteger(p.thresholdNgwee) || p.thresholdNgwee <= 0)) {
    throw new DataError({ code: 'P0001', message: 'Set the amount from which an expense needs approval.' })
  }
  const userId = await currentUserId()
  const res = await supabase.from('approval_policy').insert({
    mode: p.mode,
    expense_threshold_ngwee: p.mode === 'budgets_and_expenses_over_threshold' ? p.thresholdNgwee : 0,
    note: p.note?.trim() ? p.note.trim() : null,
    created_by: userId,
  })
  if (res.error) throw new DataError(res.error, 'Could not change the approval rule.')
}

// ---------------------------------------------------------------- departments
export interface AdminDepartment {
  id: string
  code: string
  name: string
  isCentral: boolean
  archivedAt: string | null
}

export async function listAllDepartments(): Promise<AdminDepartment[]> {
  const res = await supabase.from('department').select('id, code, name, is_central, archived_at').order('is_central', { ascending: false }).order('name')
  return (unwrap(res as never, 'Could not load the departments.') as { id: string; code: string; name: string; is_central: boolean; archived_at: string | null }[]).map((d) => ({
    id: d.id, code: d.code, name: d.name, isCentral: d.is_central, archivedAt: d.archived_at,
  }))
}

export async function createDepartment(d: { code: string; name: string }): Promise<void> {
  const res = await supabase.from('department').insert({ code: d.code.trim().toUpperCase(), name: d.name.trim() })
  if (res.error) {
    if (res.error.code === '23505') throw new DataError({ code: 'P0001', message: 'A department with that short code already exists.' })
    throw new DataError(res.error, 'Could not add the department.')
  }
}

export async function renameDepartment(id: string, name: string): Promise<void> {
  return updateDepartment(id, { name })
}

/** Change a department's name and/or short code (central treasury only). The code is stored in capitals. */
export async function updateDepartment(id: string, changes: { name?: string; code?: string }): Promise<void> {
  const patch: { name?: string; code?: string } = {}
  if (changes.name !== undefined) patch.name = changes.name.trim()
  if (changes.code !== undefined) patch.code = changes.code.trim().toUpperCase()
  const res = await supabase.from('department').update(patch).eq('id', id).select('id')
  if (res.error) {
    if (res.error.code === '23505') throw new DataError({ code: 'P0001', message: 'A department with that short code already exists.' })
    throw new DataError(res.error, 'Could not change the department.')
  }
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that department.' })
}

/**
 * Retire a department (hide it from lists and new entries) or bring it back. Nothing is deleted: its
 * members, payments, expenses and remittances stay on record. The database refuses to retire the
 * central treasury, or a department where someone who can sign in still has a role.
 */
export async function setDepartmentRetired(id: string, retired: boolean): Promise<void> {
  const res = await supabase.from('department').update({ archived_at: retired ? new Date().toISOString() : null }).eq('id', id).select('id')
  if (res.error) throw new DataError(res.error, retired ? 'Could not retire the department.' : 'Could not bring the department back.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that department.' })
}

// ---------------------------------------------------------------- spending categories
export interface AdminCategory {
  id: string
  name: string
  sortOrder: number
  archivedAt: string | null
}

export async function listAllCategories(): Promise<AdminCategory[]> {
  const res = await supabase.from('expense_category').select('id, name, sort_order, archived_at').order('sort_order').order('name')
  return (unwrap(res as never, 'Could not load the categories.') as { id: string; name: string; sort_order: number; archived_at: string | null }[]).map((c) => ({
    id: c.id, name: c.name, sortOrder: c.sort_order, archivedAt: c.archived_at,
  }))
}

export async function addCategory(name: string): Promise<void> {
  // The database treats "Transport" and "transport" as different; people do not. Refuse the lookalike.
  const same = await supabase.from('expense_category').select('id').ilike('name', name.trim().replace(/[%_\\]/g, (c) => `\\${c}`)).limit(1)
  if (!same.error && (same.data ?? []).length > 0) throw new DataError({ code: 'P0001', message: 'There is already a category with that name.' })
  const res = await supabase.from('expense_category').insert({ name: name.trim(), sort_order: 500 })
  if (res.error) {
    if (res.error.code === '23505') throw new DataError({ code: 'P0001', message: 'There is already a category with that name.' })
    throw new DataError(res.error, 'Could not add the category.')
  }
}

/** Hide a category from new entries (old expenses and budget lines keep it), or bring it back. */
export async function setCategoryArchived(id: string, archived: boolean): Promise<void> {
  const res = await supabase.from('expense_category').update({ archived_at: archived ? new Date().toISOString() : null }).eq('id', id).select('id')
  if (res.error) throw new DataError(res.error, 'Could not change the category.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that category.' })
}

// ---------------------------------------------------------------- church settings
export interface ChurchSettings {
  churchName: string
  deptCashFloatLimitNgwee: number
  remittanceSlaDays: number
  automatchWindowDays: number
  requireProofOnExpense: boolean
  requireProofOnMovement: boolean
}

type SettingRow = {
  church_name: string
  dept_cash_float_limit_ngwee: number
  remittance_sla_days: number
  automatch_window_days: number
  require_proof_on_expense: boolean
  require_proof_on_movement: boolean
}

export async function getChurchSettings(): Promise<ChurchSettings> {
  const res = await supabase.from('church_setting').select('church_name, dept_cash_float_limit_ngwee, remittance_sla_days, automatch_window_days, require_proof_on_expense, require_proof_on_movement').eq('id', true).single()
  const r = unwrap(res as never, 'Could not load the settings.') as SettingRow
  return {
    churchName: r.church_name,
    deptCashFloatLimitNgwee: Number(r.dept_cash_float_limit_ngwee),
    remittanceSlaDays: r.remittance_sla_days,
    automatchWindowDays: r.automatch_window_days,
    requireProofOnExpense: r.require_proof_on_expense,
    requireProofOnMovement: r.require_proof_on_movement,
  }
}

/** Save church settings. Only the fields given are written, so two people editing different ones do not undo each other. */
export async function updateChurchSettings(s: Partial<ChurchSettings>): Promise<void> {
  if (s.deptCashFloatLimitNgwee !== undefined && (!Number.isSafeInteger(s.deptCashFloatLimitNgwee) || s.deptCashFloatLimitNgwee < 0)) {
    throw new DataError({ code: 'P0001', message: 'The cash limit must be a whole amount, zero or more.' })
  }
  const userId = await currentUserId()
  const res = await supabase
    .from('church_setting')
    .update({
      ...(s.churchName !== undefined ? { church_name: s.churchName.trim() } : {}),
      ...(s.deptCashFloatLimitNgwee !== undefined ? { dept_cash_float_limit_ngwee: s.deptCashFloatLimitNgwee } : {}),
      ...(s.remittanceSlaDays !== undefined ? { remittance_sla_days: s.remittanceSlaDays } : {}),
      ...(s.automatchWindowDays !== undefined ? { automatch_window_days: s.automatchWindowDays } : {}),
      ...(s.requireProofOnExpense !== undefined ? { require_proof_on_expense: s.requireProofOnExpense } : {}),
      ...(s.requireProofOnMovement !== undefined ? { require_proof_on_movement: s.requireProofOnMovement } : {}),
      updated_by: userId,
    })
    .eq('id', true)
    .select('id')
  if (res.error) throw new DataError(res.error, 'Could not save the settings.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change the settings.' })
}

// ---------------------------------------------------------------- people
export type Role = 'admin' | 'auditor' | 'dept_treasurer' | 'dept_viewer'

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Central treasury (administrator)',
  auditor: 'Auditor (looks at everything)',
  dept_treasurer: 'Department treasurer',
  dept_viewer: 'Department viewer (looks only)',
}

export interface Person {
  id: string
  fullName: string
  email: string
  phone: string | null
  isActive: boolean
  mustChangePassword: boolean
  /** The owner of the app: only the owner can change most things about this account. */
  isOwner: boolean
  roles: { id: string; role: Role; departmentId: string | null }[]
}

type PersonRow = {
  id: string
  full_name: string
  email: string
  phone: string | null
  is_active: boolean
  must_change_password: boolean
  roles: { id: string; role: Role; department_id: string | null; revoked_at: string | null }[]
}

/** Everyone with an account, with the roles they hold now. */
export async function listPeople(): Promise<Person[]> {
  // user_role points at app_user three ways (the person, who granted, who revoked): name the one we mean.
  const res = await supabase
    .from('app_user')
    .select('id, full_name, email, phone, is_active, must_change_password, roles:user_role!user_id(id, role, department_id, revoked_at)')
    .order('full_name')
  // Who is the owner? Only to hide buttons that would be refused; any trouble just means "not known".
  const ownerRes = await supabase.rpc('owner_account_id')
  const ownerId = ownerRes.error ? null : ((ownerRes.data as string | null) ?? null)
  return (unwrap(res as never, 'Could not load the people.') as PersonRow[]).map((r) => ({
    id: r.id,
    isOwner: ownerId !== null && r.id === ownerId,
    fullName: r.full_name,
    email: r.email,
    phone: r.phone,
    isActive: r.is_active,
    mustChangePassword: r.must_change_password,
    roles: (r.roles ?? []).filter((x) => x.revoked_at === null).map((x) => ({ id: x.id, role: x.role, departmentId: x.department_id })),
  }))
}

/** Switch an account off or on. Nobody is ever deleted: their records belong to the department. */
export async function setPersonActive(id: string, active: boolean): Promise<void> {
  const res = await supabase.from('app_user').update({ is_active: active }).eq('id', id).select('id')
  if (res.error) throw new DataError(res.error, 'Could not change the account.')
  if (((res.data as unknown[]) ?? []).length === 0) throw new DataError({ code: 'P0001', message: 'You cannot change that account.' })
}

type AdminUsersBody =
  | { action: 'create'; email: string; full_name: string; phone?: string; role: Role; department_id?: string | null }
  | { action: 'reset_password'; user_id: string }
  | { action: 'grant_role'; user_id: string; role: Role; department_id?: string | null }
  | { action: 'revoke_role'; role_id: string }

/** Ask the admin-users Edge Function to do something, and turn its answer into plain words. */
export async function callAdminUsers<T extends Record<string, unknown>>(body: AdminUsersBody): Promise<T> {
  const res = await supabase.functions.invoke('admin-users', { body })
  if (res.error) {
    let message = ''
    const ctx = (res.error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        message = String(((await ctx.json()) as { error?: string }).error ?? '')
      } catch {
        message = ''
      }
    }
    // A missing function (not deployed yet) comes back as a network error or a 404.
    const status = ctx?.status
    if (!message && (status === 404 || /failed to send|fetch/i.test(res.error.message))) {
      message = 'The account service is not switched on yet. Ask whoever set up the system to deploy it.'
    }
    throw new DataError({ code: 'P0001', message: message || 'Could not complete that. Please try again.' })
  }
  return res.data as T
}

export const createPerson = (p: { email: string; fullName: string; phone?: string; role: Role; departmentId?: string | null }) =>
  callAdminUsers<{ user_id: string; temp_password: string }>({ action: 'create', email: p.email, full_name: p.fullName, phone: p.phone, role: p.role, department_id: p.departmentId ?? null })
export const resetPassword = (userId: string) => callAdminUsers<{ temp_password: string }>({ action: 'reset_password', user_id: userId })
export const grantRole = (userId: string, role: Role, departmentId?: string | null) =>
  callAdminUsers<{ ok: true }>({ action: 'grant_role', user_id: userId, role, department_id: departmentId ?? null })
export const revokeRole = (roleId: string) => callAdminUsers<{ ok: true }>({ action: 'revoke_role', role_id: roleId })
